import React, { CSSProperties, useContext, useMemo } from 'react';

import { TeamContext, AuthContext } from 'contexts';
import { TitleProps } from 'components/Typography/Title';
import styled from 'styled-components';

import type { DashboardType } from '../../Router/pages/MyBusinessPage';
import { Title } from '../Typography';
import Space from '../Space';
import Dropdown from '../Dropdown';
import Icon from '../Icon';

const TitleDropdown: React.FC<{
  type: 'dashboard' | 'team-settings' | 'team-card';
  mainTitleLevel?: TitleProps['level'];
  mainTitleStyle?: CSSProperties;
  itemTitleLevel?: TitleProps['level'];
  dashboardType?: DashboardType;
  setDashboardType?: (value: DashboardType) => void;
}> = ({
  type,
  mainTitleLevel,
  mainTitleStyle,
  itemTitleLevel,
  dashboardType,
  setDashboardType,
}) => {
  const { availableTeamStates, targetTeamState, setTargetTeamId } =
    useContext(TeamContext);

  const { auth } = useContext(AuthContext);

  const isDashboard = type === 'dashboard';
  const isTeamDashboard = isDashboard && dashboardType === 'team';
  const isUserDashboard = isDashboard && dashboardType === 'me';

  const userState = {
    // needs to be a string that is not undefined or null
    // otherwise antd's dropdown will convert this to a string
    // and on the case of falsy values it will be a tmp value
    id: 'user',
    name: auth.firstName,
  };

  const options = [
    ...(isDashboard && !isUserDashboard && auth.isLicensed ? [userState] : []),
    ...availableTeamStates.filter(
      x => isUserDashboard || x.id !== targetTeamState.id,
    ),
  ];

  const title = useMemo(() => {
    if (isDashboard) {
      return isUserDashboard
        ? `${auth.firstName}'s Dashboard`
        : `${targetTeamState.name} Dashboard`;
    }

    const postfix = type === 'team-settings' ? 'Settings' : '';
    return availableTeamStates.length > 1
      ? `${targetTeamState.name} ${postfix}`
      : `Team ${postfix}`;
  }, [
    type,
    isDashboard,
    isUserDashboard,
    availableTeamStates.length,
    targetTeamState.name,
    auth.firstName,
  ]);

  return (
    <Dropdown
      trigger={['click']}
      getPopupContainer={trigger => trigger}
      menu={{
        style: { margin: 0, padding: 0 },
        onClick: ({ key }: any) => {
          if (isDashboard && setDashboardType) {
            setDashboardType(key === 'user' ? 'me' : 'team');
          }

          if (key !== 'user') setTargetTeamId(key);
        },
        items: options.map(option => ({
          key: option.id,
          label: itemTitleLevel ? (
            <Title level={itemTitleLevel} hideMargin>
              {option.name}
            </Title>
          ) : (
            option.name
          ),
        })),
      }}
    >
      <ClickableSpace>
        <Title
          hideMargin
          style={mainTitleStyle}
          level={mainTitleLevel ?? 1}
          data-cy={dashboardType ? 'dashboard-title' : 'teamSettings-teamName'}
        >
          {title}
        </Title>
        {Boolean(options.length) && <ArrowDownIcon />}
      </ClickableSpace>
    </Dropdown>
  );
};

export default TitleDropdown;

const ClickableSpace = styled(Space)`
  cursor: pointer;
`;

const ArrowDownIcon = styled(Icon).attrs({
  type: 'arrowDownThin',
  color: 'black',
  height: 8,
})`
  display: block;
`;
